import React, { useEffect, useMemo, useState } from "react";
import { Box, Dialog, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { History } from "lucide-react";
import CustomTable from "../../common/CustomTable";
import { Column, SingleAlertInfo, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { ProductItemInfo, activateProductItem, getProductItemHistory, useProductReducer } from "../../../redux/slice/product/createProductSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomIconButton from "../../common/CustomIconButton";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { useModalStyles } from "../../common/useModalStyles";

interface ProductItemHistoryProps {
    open: boolean;
    onClose: () => void;
}

interface Row {
    sku: string;
    inactiveDate: string;
    size: string;
    units: string;
    action: JSX.Element;
  }

const ProductItemHistoryPopup: React.FC<ProductItemHistoryProps> = ({
    open,
    onClose
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        cancelBtnSx,
    } = useModalStyles();
    const dispatch = useAppDispatch();
    const { productItemsHistory, productObject } = useProductReducer();
    const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: "", show: false });

    const columnsPriceItem: Column[] = useMemo(() => {
        return [
            { id: "sku", label: "SKU", minWidth: 100 },
            { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
            { id: "size", label: "Size", minWidth: 100 },
            { id: "units", label: "# of Units", minWidth: 100 },
            { id: "action", label: "Action", minWidth: 100 },
        ]
    }, [open]);

    useEffect(() => {
        if (open) {
            dispatch(getProductItemHistory({productId: productObject.productId}))
        }
    }, [open])

    const handleAlert = (message: string, type: "success" | "error" | "info") => {
        const alertObj: SingleAlertInfo = {
            message,
            alertType: type,
        };
        dispatch(setSingleAlertObj(alertObj));
    };

    const activateProductItemData = (row: ProductItemInfo) => {
        setShowOptionsPopup({
            message: "Do you really want to activate this product item ? ",
            show: true,
            variant: "confirm",
            buttons: [
                {
                    name: "Confirm",
                    color: "primary",
                    onClick: () => handleActionActiveProductItem(row),
                },
                { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
            ],
        });
    }

    const handleActionActiveProductItem = async (row: ProductItemInfo) => {

        const data = { productItemId: row.productItemId }
        setShowOptionsPopup({ message: "", show: false });

        try {
            const response = await dispatch(activateProductItem(data));

            if (response.payload.message === "SUCCESS") {
                dispatch(getProductItemHistory({ productId: productObject.productId }))
                handleAlert(`Product Item activated successfully.`, "success");
                return response;
            } else {
                throw new Error("Failed to activate product item.");
            }
        } catch (error) {
            handleAlert("Failed to activate product item.", "error");
            throw error;
        }
    };

    const actionComponent = (row: ProductItemInfo): JSX.Element => {
        return (
          <>
              <CustomIconButton
                color="success"
                size="small"
                variant="outlined"
                tooltipMessage="Activate Product Item"
                tooltipPlacement="top"
                onClick={() => activateProductItemData(row)}
              >
                <CheckCircleOutline fontSize="small" />
              </CustomIconButton>
          </>
        );
      };
    
    const productItemsHistoryData: Row[] = productItemsHistory ? productItemsHistory.map((item: ProductItemInfo) => ({
        sku: item.sku,
        inactiveDate: item.inactiveDate,
        size: item.size,
        units: item.units,
        action: actionComponent(item),
    })) : [];

    const cancelOptionPopup = () => {
        setShowOptionsPopup({ message: "", show: false });
    }

    return (
        <>
            <Dialog
                open={open}
                keepMounted
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                scroll="paper"
                PaperProps={{ sx: paperSx(isMobile) }}
                onClick={(event) => {
                    event.stopPropagation();
                }}
                aria-describedby="product-item-history-dialog-description"
            >
                {showOptionPopup.show && (
                    <OptionsPopup
                        open={showOptionPopup.show}
                        onClose={() => setShowOptionsPopup({ message: "", show: false })}
                        variant={showOptionPopup.variant}
                        message={<>{showOptionPopup.message}</>}
                        buttons={showOptionPopup.buttons}
                        title={showOptionPopup.title}
                        obj={showOptionPopup.obj}
                    />
                )}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={headerSx}>
                        <Box sx={iconBoxSx(tokens.accent)}>
                            <History size={20} color={tokens.accent} strokeWidth={1.8} />
                        </Box>
                        <Box sx={{ flex: 1, pr: 4 }}>
                            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                                Item History
                            </Typography>
                            <Typography id="product-item-history-dialog-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                                View inactive product items and reactivate when needed.
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            ...bodySx,
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            scrollbarGutter: "stable",
                            "&::-webkit-scrollbar": { width: 6, height: 6 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": {
                                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                                borderRadius: 4,
                            },
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <CustomTable
                                    columns={columnsPriceItem}
                                    rows={productItemsHistoryData}
                                    disablePagination={true}
                                    borderBox={true}
                                    sx={{
                                        width: "100%",
                                        maxWidth: "100%",
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={footerSx}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1.5,
                                flexDirection: { xs: "column", sm: "row" },
                            }}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={onClose}
                                sx={{
                                    ...cancelBtnSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    px: 3,
                                    py: 1.2,
                                    border: "1px solid",
                                    cursor: "pointer",
                                    order: { xs: 2, sm: 1 },
                                }}
                            >
                                Cancel
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default ProductItemHistoryPopup;
